import React, { useCallback, useEffect, useRef } from 'react';
import type { ThemeProps } from '@models/types';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';

const BLUR_PX = 18;

const OVAL_WIDTH_RATIO = 0.28;
const OVAL_HEIGHT_RATIO = 0.578;
const OVAL_BORDER_RADIUS = 400;

/**
 * Safari does not support CanvasRenderingContext2D.filter (blur).
 * We use two stacked canvases: background with CSS filter for blur (Safari-compatible),
 * foreground with transparent background and oval cutout for clear video.
 */
const canvasCommonSx = {
    display: 'flex',
    position: 'absolute' as const,
    top: 0,
    width: '100%',
    height: '100%',
    left: '0',
    objectFit: 'cover' as const,
    objectPosition: 'center',
    borderRadius: '32px',
    transform: 'scaleX(-1)',
};

function addRoundedRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number,
) {
    const r = Math.min(radius, width / 2, height / 2);

    ctx.moveTo(x + r, y);
    ctx.lineTo(x + width - r, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + r);
    ctx.lineTo(x + width, y + height - r);
    ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
    ctx.lineTo(x + r, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);

    return r;
}

type MaskProps = {
    video?: HTMLVideoElement | null;
    isLoading: boolean;
    isError: boolean;
    onReady?: () => void;
};

export const Mask = ({ isLoading, video, isError, onReady }: MaskProps) => {
    const backgroundCanvasRef = useRef<HTMLCanvasElement>(null);
    const foregroundCanvasRef = useRef<HTMLCanvasElement>(null);
    const rafRef = useRef<number>(0);
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const isInitialized = useRef(false);

    const drawFrame = useCallback(
        (
            vid: HTMLVideoElement,
            backgroundCanvas: HTMLCanvasElement,
            foregroundCanvas: HTMLCanvasElement,
        ) => {
            const w = vid.videoWidth;
            const h = vid.videoHeight;

            if (w === 0 || h === 0) {
                return;
            }

            const modifiers = {
                x: matchesMobileSm ? 40 : 100,
                y: matchesMobileSm ? 20 : 80,
            };
            const cx = w / 2;
            const cy = h / 2;

            // Use min dimension so oval keeps consistent proportions in portrait (e.g. 768×1024) and landscape
            const refSize = Math.min(w, h);
            const scale = 1.38;
            const halfW = (refSize * OVAL_WIDTH_RATIO * scale) / 2;
            const halfH = (refSize * OVAL_HEIGHT_RATIO * scale) / 2;
            const x = cx - halfW;
            const y = cy - halfH;
            const width = halfW * 2;
            const height = halfH * 2;
            // Oval rect with modifiers (same as in addRoundedRectPath below)
            const ovalLeft = x - modifiers.x / 2;
            const ovalTop = y + modifiers.y / 2;
            const ovalWidth = width + modifiers.x;
            const ovalHeight = height - modifiers.y;

            // 1) Background canvas: full frame only (blur via CSS for Safari support)
            if (backgroundCanvas.width !== w || backgroundCanvas.height !== h) {
                backgroundCanvas.width = w;
                backgroundCanvas.height = h;
            }
            const bgCtx = backgroundCanvas.getContext('2d');

            if (bgCtx) {
                bgCtx.drawImage(vid, 0, 0, w, h);
            }

            // 2) Foreground canvas: transparent, oval clip with clear video
            if (foregroundCanvas.width !== w || foregroundCanvas.height !== h) {
                foregroundCanvas.width = w;
                foregroundCanvas.height = h;
            }
            const ctx = foregroundCanvas.getContext('2d');

            if (!ctx) {
                return;
            }

            ctx.clearRect(0, 0, w, h);

            ctx.save();
            ctx.beginPath();
            const r = addRoundedRectPath(
                ctx,
                ovalLeft,
                ovalTop,
                ovalWidth,
                ovalHeight,
                OVAL_BORDER_RADIUS,
            );

            ctx.closePath();
            ctx.clip();
            ctx.drawImage(vid, 0, 0, w, h);
            ctx.restore();

            if (isError) {
                ctx.strokeStyle = theme.palette.secondaryA4;
            } else if (isLoading) {
                ctx.strokeStyle = theme.palette.secondaryA3;
            } else {
                ctx.strokeStyle = theme.palette.primaryA7;
            }

            // Ellipses aligned to mask: same X as right-side corners, Y at top-right and bottom-right corner centers
            const ellipseCenterX = ovalLeft + ovalWidth - r;
            const ellipseTopY = ovalTop + r;
            const ellipseBottomY = ovalTop + ovalHeight - r;

            const offsetY = matchesMobileSm ? 6 : 13;

            // top part (aligns with mask top-right corner)
            ctx.beginPath();
            ctx.ellipse(ellipseCenterX, ellipseTopY - offsetY, r, r, 0, 0, Math.PI, true);
            ctx.lineWidth = matchesMobileSm ? 3 : 6;
            ctx.stroke();

            // bottom part (aligns with mask bottom-right corner)
            ctx.beginPath();
            ctx.ellipse(ellipseCenterX, ellipseBottomY + offsetY, r, r, 0, 0, Math.PI, false);
            ctx.lineWidth = matchesMobileSm ? 3 : 6;
            ctx.stroke();

            if (!isInitialized.current) {
                isInitialized.current = true;
                onReady?.();
            }
        },
        [isLoading, theme, matchesMobileSm, isError, onReady],
    );

    const tick = useCallback(() => {
        const backgroundCanvas = backgroundCanvasRef.current;
        const foregroundCanvas = foregroundCanvasRef.current;

        if (!backgroundCanvas || !foregroundCanvas || !video) {
            return;
        }
        drawFrame(video, backgroundCanvas, foregroundCanvas);
        rafRef.current = requestAnimationFrame(tick);
    }, [drawFrame, video]);

    useEffect(() => {
        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [tick]);

    return (
        <React.Fragment>
            {/* Background: full frame, blur applied via CSS (works in Safari) */}
            <Box
                component='canvas'
                sx={{
                    ...canvasCommonSx,
                    filter: `blur(${BLUR_PX}px)`,
                }}
                width={video?.videoWidth}
                height={video?.videoHeight}
                ref={backgroundCanvasRef}
            />
            {/* Foreground: transparent with oval cutout for clear video + border */}
            <Box
                component='canvas'
                sx={canvasCommonSx}
                width={video?.videoWidth}
                height={video?.videoHeight}
                ref={foregroundCanvasRef}
            />
        </React.Fragment>
    );
};
